import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * WebRTCDiagnostics — single-page WebRTC reachability test.
 *
 * URL:  /diagnostics/webrtc/:roomId?t=<token>
 *
 * Runs the full ICE-gathering flow against the *exact same* TURN config the
 * real meeting room would use, then shows a plain-English summary of which
 * candidate types reached the browser. The broker can open this on each of
 * the failing devices and copy/paste the summary back to support — no
 * DevTools, no console.
 *
 * What we test:
 *   1. Can we fetch the TURN credentials endpoint at all?
 *   2. Did the server hand us Metered.ca creds (vs falling back to OpenRelay)?
 *   3. Does the browser gather any host candidates (LAN reachability)?
 *   4. Does the browser gather any srflx candidates (STUN reachable)?
 *   5. Does the browser gather any relay candidates (TURN reachable)?
 *   6. Per ICE server URL: did at least one relay candidate come from it?
 */
const WebRTCDiagnostics = () => {
  const { roomId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') || params.get('token') || '';

  const [phase, setPhase] = useState('init'); // init | fetching | gathering | done | error
  const [iceServers, setIceServers] = useState(null);
  const [error, setError] = useState(null);
  // candidates: array of { type, protocol, address, port, raw, server (turn url that produced this) }
  const [candidates, setCandidates] = useState([]);
  const [gatheringMs, setGatheringMs] = useState(0);
  const [browserInfo, setBrowserInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pc = null;
    let startTs = 0;
    let progressTimer = null;

    const run = async () => {
      try {
        // 0. Browser info
        const ua = navigator.userAgent;
        const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
        const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
        setBrowserInfo({
          userAgent: ua,
          isSafari,
          isIOS,
          isMobile: /Mobile|Android|iP(hone|od|ad)/.test(ua),
          online: navigator.onLine,
          rtcPeerConnection: !!window.RTCPeerConnection,
          getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        });

        if (!roomId || !token) {
          throw new Error('Missing room or token in URL. Use the link your broker sent you and replace `/join/` with `/diagnostics/webrtc/`.');
        }

        // 1. Fetch TURN config from the same endpoint the real meeting uses.
        setPhase('fetching');
        const r = await fetch(`${BACKEND}/api/public/meetings/${roomId}/turn-credentials?token=${encodeURIComponent(token)}`);
        if (!r.ok) {
          throw new Error(`TURN endpoint returned HTTP ${r.status}. Either the meeting expired or the link is stale.`);
        }
        const data = await r.json();
        if (cancelled) return;
        const servers = data.iceServers || [];
        setIceServers(servers);
        if (!servers.length) {
          throw new Error('Server returned an empty iceServers list. Backend env vars likely missing.');
        }

        // 2. Spin up an RTCPeerConnection just to gather candidates. No media,
        // no offer to peer — we just want to see which candidate types the
        // browser can produce on this network.
        setPhase('gathering');
        pc = new RTCPeerConnection({ iceServers: servers, iceTransportPolicy: 'all' });
        // Need at least one m-line to trigger ICE gathering. A data channel is
        // the cheapest way (no media permission prompt).
        pc.createDataChannel('diag');

        pc.addEventListener('icecandidate', (evt) => {
          if (!evt.candidate) return; // null = gathering complete signal
          const c = evt.candidate;
          // Try to figure out which server URL this relay came from. We can
          // only do this by matching the relay address against the TURN URL,
          // which is fuzzy — instead show the URL field on the candidate itself
          // when the browser populates it (Chrome does, Safari doesn't).
          const rec = {
            type: c.type || 'unknown',
            protocol: c.protocol || '',
            address: c.address || c.ip || '',
            port: c.port || 0,
            url: evt.url || c.url || '',  // populated when relay comes from a TURN server
            raw: c.candidate,
          };
          setCandidates((prev) => [...prev, rec]);
        });

        pc.addEventListener('icegatheringstatechange', () => {
          if (pc.iceGatheringState === 'complete') {
            // Stop after gather completes so we can summarize
            setPhase('done');
            const elapsed = Date.now() - startTs;
            setGatheringMs(elapsed);
            try { pc.close(); } catch (e) { /* */ }
            if (progressTimer) clearInterval(progressTimer);
          }
        });

        // Trigger gathering
        startTs = Date.now();
        progressTimer = setInterval(() => {
          if (cancelled) return;
          setGatheringMs(Date.now() - startTs);
        }, 250);
        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);

        // Hard cut-off after 15 seconds — slow networks can drag this out
        // and we'd rather show partial results than hang the user.
        setTimeout(() => {
          if (cancelled) return;
          if (pc && pc.iceGatheringState !== 'complete') {
            setPhase('done');
            setGatheringMs(Date.now() - startTs);
            try { pc.close(); } catch (e) { /* */ }
            if (progressTimer) clearInterval(progressTimer);
          }
        }, 15_000);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || String(e));
        setPhase('error');
      }
    };

    run();
    return () => {
      cancelled = true;
      if (progressTimer) clearInterval(progressTimer);
      try { pc?.close(); } catch (e) { /* */ }
    };
    // roomId/token are stable for the lifetime of this page; intentionally no other deps.
  }, [roomId, token]);

  // ── Summary derivation ──
  const hostCount = candidates.filter((c) => c.type === 'host').length;
  const srflxCount = candidates.filter((c) => c.type === 'srflx').length;
  const relayCount = candidates.filter((c) => c.type === 'relay').length;
  const meteredRelay = candidates.find((c) => c.type === 'relay' && (c.url?.includes('metered.ca') || c.raw?.includes('metered')));
  const usingMetered = iceServers?.some((s) => {
    const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
    return urls.some((u) => typeof u === 'string' && u.includes('global.relay.metered.ca'));
  });

  const buildReport = () => {
    const lines = [];
    lines.push('=== WebRTC Diagnostic Report ===');
    lines.push(`Room: ${roomId}`);
    lines.push(`When: ${new Date().toISOString()}`);
    lines.push('');
    lines.push('--- Browser ---');
    if (browserInfo) {
      lines.push(`UA: ${browserInfo.userAgent}`);
      lines.push(`Safari: ${browserInfo.isSafari} | iOS: ${browserInfo.isIOS} | Mobile: ${browserInfo.isMobile} | Online: ${browserInfo.online}`);
    }
    lines.push('');
    lines.push('--- ICE Servers from backend ---');
    (iceServers || []).forEach((s) => {
      const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
      urls.forEach((u) => lines.push(`  ${u}${s.username ? `  user=${s.username.slice(0, 8)}…` : ''}`));
    });
    lines.push(`Metered.ca config: ${usingMetered ? 'YES' : 'NO (using fallback)'}`);
    lines.push('');
    lines.push('--- ICE candidates gathered ---');
    lines.push(`host:  ${hostCount}`);
    lines.push(`srflx: ${srflxCount}  (STUN reachable: ${srflxCount > 0 ? 'YES' : 'NO'})`);
    lines.push(`relay: ${relayCount}  (TURN reachable: ${relayCount > 0 ? 'YES' : 'NO'})`);
    lines.push(`Gathering time: ${gatheringMs}ms`);
    lines.push('');
    lines.push('--- Full candidate list ---');
    candidates.forEach((c, i) => {
      lines.push(`[${i}] type=${c.type} proto=${c.protocol} addr=${c.address}:${c.port}${c.url ? ` url=${c.url}` : ''}`);
    });
    lines.push('');
    lines.push('--- Diagnosis ---');
    if (relayCount > 0) {
      lines.push('OK — TURN relay reachable. Calls should work even across NATs.');
    } else if (srflxCount > 0) {
      lines.push('PARTIAL — STUN works but TURN does not. This network is blocking TURN ports 80/443. Calls will fail when both peers are behind symmetric NATs (typical cellular ↔ home WiFi).');
    } else if (hostCount > 0) {
      lines.push('SEVERE — Only LAN candidates. The browser cannot reach the public internet for WebRTC traffic. Firewall/proxy is blocking UDP entirely.');
    } else {
      lines.push('NO CANDIDATES — Network blocking all ICE traffic.');
    }
    return lines.join('\n');
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildReport());
      // eslint-disable-next-line no-alert
      alert('Diagnostic report copied. Paste it into the chat with your support contact.');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Could not copy automatically. The report is in a textbox below — select all and copy manually.');
    }
  };

  // ── UI ──
  const StatusRow = ({ ok, label, sub }) => (
    <div className={`flex items-start gap-3 rounded-lg p-3 border ${ok ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`}>
      {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />}
      <div>
        <p className={`font-semibold ${ok ? 'text-emerald-100' : 'text-rose-100'}`}>{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  const PendingRow = ({ label }) => (
    <div className="flex items-start gap-3 rounded-lg p-3 border bg-slate-800/60 border-slate-700">
      <Loader2 className="h-5 w-5 text-slate-300 mt-0.5 shrink-0 animate-spin" />
      <p className="font-semibold text-slate-200">{label}</p>
    </div>
  );

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-page text-slate-900 p-6 flex items-center justify-center">
        <div className="max-w-md w-full bg-slate-900 border border-rose-500/40 rounded-xl p-6">
          <AlertTriangle className="h-8 w-8 text-rose-400 mb-3" />
          <h1 className="text-xl font-bold mb-2">Diagnostic failed</h1>
          <p className="text-sm text-slate-300 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="diag-retry-btn">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page text-slate-900 p-4 sm:p-8" data-testid="webrtc-diagnostics-page">
      <div className="max-w-2xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">WebRTC Connection Diagnostic</h1>
          <p className="text-sm text-slate-400 mt-1">Room {roomId} · run this on every device that's failing</p>
        </header>

        <div className="space-y-3">
          {/* Step 1 — TURN endpoint */}
          {phase === 'init' || phase === 'fetching' ? (
            <PendingRow label="Fetching TURN configuration from server…" />
          ) : (
            <StatusRow
              ok={!!iceServers?.length}
              label={`TURN config fetched (${iceServers?.length || 0} ICE servers)`}
              sub={usingMetered ? 'Using Metered.ca production relay' : 'Using public-fallback relays (env vars missing or not loaded)'}
            />
          )}

          {/* Step 2 — gathering */}
          {phase === 'gathering' && (
            <PendingRow label={`Gathering ICE candidates (${(gatheringMs / 1000).toFixed(1)}s)…`} />
          )}

          {phase === 'done' && (
            <>
              <StatusRow
                ok={hostCount > 0}
                label={`Host candidates: ${hostCount}`}
                sub="Browser can describe its own LAN address"
              />
              <StatusRow
                ok={srflxCount > 0}
                label={`STUN reflexive candidates: ${srflxCount}`}
                sub={srflxCount > 0 ? 'STUN servers reachable — your public IP was discovered' : 'STUN unreachable — UDP egress likely blocked'}
              />
              <StatusRow
                ok={relayCount > 0}
                label={`TURN relay candidates: ${relayCount}`}
                sub={relayCount > 0
                  ? 'TURN relay reachable — calls should work across any NAT'
                  : 'TURN relay UNREACHABLE — this is the cause of call failures'}
              />

              <div className={`rounded-xl p-5 border-2 mt-4 ${
                relayCount > 0
                  ? 'bg-emerald-500/10 border-emerald-500/60'
                  : srflxCount > 0
                  ? 'bg-amber-500/10 border-amber-500/60'
                  : 'bg-rose-500/10 border-rose-500/60'
              }`}>
                <h2 className="text-lg font-bold mb-2">Verdict</h2>
                {relayCount > 0 ? (
                  <div className="text-emerald-100 text-sm space-y-2">
                    <p>✅ Your network can reach the TURN relay. The call setup should succeed.</p>
                    <p className="text-slate-300">If calls are still failing on this device, the issue is most likely browser/cache. Try a hard reload (Ctrl+Shift+R or, on iOS, close all Safari tabs and reopen).</p>
                  </div>
                ) : srflxCount > 0 ? (
                  <div className="text-amber-100 text-sm space-y-2">
                    <p>⚠️ STUN works on this network but TURN does not. Your firewall/carrier is blocking the relay ports.</p>
                    <p className="text-slate-300">This is why your calls cut out when one device is on cellular and the other on home WiFi. Try:</p>
                    <ul className="list-disc list-inside text-slate-300 mt-1 space-y-1">
                      <li>Different network (mobile hotspot / 4G instead of corporate WiFi)</li>
                      <li>Different browser — Safari is sometimes stricter than Chrome on iOS</li>
                      <li>Contact your IT to allow outbound TCP/UDP on ports 443 to <code>global.relay.metered.ca</code></li>
                    </ul>
                  </div>
                ) : hostCount > 0 ? (
                  <div className="text-rose-100 text-sm">
                    <p>❌ This network blocks all public WebRTC traffic. Only LAN candidates were gathered.</p>
                    <p className="text-slate-300 mt-2">Try a different network (cellular hotspot is usually a good test).</p>
                  </div>
                ) : (
                  <div className="text-rose-100 text-sm">
                    <p>❌ No ICE candidates at all were gathered. Browser or network is fully blocking WebRTC.</p>
                  </div>
                )}
              </div>

              <details className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-4">
                <summary className="cursor-pointer text-sm text-slate-300 select-none">Show raw candidate list ({candidates.length} entries)</summary>
                <pre className="text-[10px] text-slate-400 mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-all" data-testid="diag-candidate-list">
                  {candidates.map((c, i) => `[${i}] ${c.type.padEnd(5)} ${c.protocol.padEnd(4)} ${c.address}:${c.port}${c.url ? ` url=${c.url}` : ''}`).join('\n')}
                </pre>
              </details>

              <details className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-2">
                <summary className="cursor-pointer text-sm text-slate-300 select-none">Show ICE servers received from backend</summary>
                <pre className="text-[10px] text-slate-400 mt-3 max-h-48 overflow-auto whitespace-pre-wrap" data-testid="diag-ice-servers">
                  {JSON.stringify(iceServers, null, 2)}
                </pre>
              </details>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button onClick={copyReport} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="diag-copy-report-btn">
                  <Copy className="h-4 w-4 mr-2" /> Copy full report
                </Button>
                <Button onClick={() => window.location.reload()} variant="outline" className="bg-slate-800 hover:bg-slate-700 text-white border-slate-700 flex-1" data-testid="diag-rerun-btn">
                  Re-run test
                </Button>
              </div>

              <textarea
                readOnly
                value={buildReport()}
                className="mt-4 w-full h-40 bg-slate-900 border border-slate-700 rounded-lg p-3 text-[10px] text-slate-300 font-mono"
                data-testid="diag-report-textarea"
                onFocus={(e) => e.target.select()}
              />
            </>
          )}
        </div>

        <footer className="mt-10 text-xs text-slate-500">
          <p>This page is safe to share — it doesn't transmit your video or audio. It only checks whether your browser can reach the relay servers our meeting platform uses.</p>
        </footer>
      </div>
    </div>
  );
};

export default WebRTCDiagnostics;
